import { useMemo, useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";

/**
 * Curated country-code catalog — sorted by country name.
 * Covers all G20 + key business markets. Add more if a client requests it.
 */
export const COUNTRY_CODES = [
  { code: "+93",  name: "Afghanistan",            iso: "AF" },
  { code: "+54",  name: "Argentina",              iso: "AR" },
  { code: "+61",  name: "Australia",              iso: "AU" },
  { code: "+43",  name: "Austria",                iso: "AT" },
  { code: "+880", name: "Bangladesh",             iso: "BD" },
  { code: "+32",  name: "Belgium",                iso: "BE" },
  { code: "+55",  name: "Brazil",                 iso: "BR" },
  { code: "+1",   name: "Canada",                 iso: "CA" },
  { code: "+56",  name: "Chile",                  iso: "CL" },
  { code: "+86",  name: "China",                  iso: "CN" },
  { code: "+57",  name: "Colombia",               iso: "CO" },
  { code: "+45",  name: "Denmark",                iso: "DK" },
  { code: "+20",  name: "Egypt",                  iso: "EG" },
  { code: "+358", name: "Finland",                iso: "FI" },
  { code: "+33",  name: "France",                 iso: "FR" },
  { code: "+49",  name: "Germany",                iso: "DE" },
  { code: "+30",  name: "Greece",                 iso: "GR" },
  { code: "+852", name: "Hong Kong",              iso: "HK" },
  { code: "+91",  name: "India",                  iso: "IN" },
  { code: "+62",  name: "Indonesia",              iso: "ID" },
  { code: "+98",  name: "Iran",                   iso: "IR" },
  { code: "+353", name: "Ireland",                iso: "IE" },
  { code: "+972", name: "Israel",                 iso: "IL" },
  { code: "+39",  name: "Italy",                  iso: "IT" },
  { code: "+81",  name: "Japan",                  iso: "JP" },
  { code: "+254", name: "Kenya",                  iso: "KE" },
  { code: "+82",  name: "Korea, South",           iso: "KR" },
  { code: "+965", name: "Kuwait",                 iso: "KW" },
  { code: "+60",  name: "Malaysia",               iso: "MY" },
  { code: "+52",  name: "Mexico",                 iso: "MX" },
  { code: "+212", name: "Morocco",                iso: "MA" },
  { code: "+31",  name: "Netherlands",            iso: "NL" },
  { code: "+64",  name: "New Zealand",            iso: "NZ" },
  { code: "+234", name: "Nigeria",                iso: "NG" },
  { code: "+47",  name: "Norway",                 iso: "NO" },
  { code: "+968", name: "Oman",                   iso: "OM" },
  { code: "+92",  name: "Pakistan",               iso: "PK" },
  { code: "+51",  name: "Peru",                   iso: "PE" },
  { code: "+63",  name: "Philippines",            iso: "PH" },
  { code: "+48",  name: "Poland",                 iso: "PL" },
  { code: "+351", name: "Portugal",               iso: "PT" },
  { code: "+974", name: "Qatar",                  iso: "QA" },
  { code: "+40",  name: "Romania",                iso: "RO" },
  { code: "+7",   name: "Russia",                 iso: "RU" },
  { code: "+966", name: "Saudi Arabia",           iso: "SA" },
  { code: "+65",  name: "Singapore",              iso: "SG" },
  { code: "+27",  name: "South Africa",           iso: "ZA" },
  { code: "+34",  name: "Spain",                  iso: "ES" },
  { code: "+94",  name: "Sri Lanka",              iso: "LK" },
  { code: "+46",  name: "Sweden",                 iso: "SE" },
  { code: "+41",  name: "Switzerland",            iso: "CH" },
  { code: "+886", name: "Taiwan",                 iso: "TW" },
  { code: "+66",  name: "Thailand",               iso: "TH" },
  { code: "+90",  name: "Turkey",                 iso: "TR" },
  { code: "+971", name: "UAE",                    iso: "AE" },
  { code: "+44",  name: "United Kingdom",         iso: "GB" },
  { code: "+1",   name: "United States",          iso: "US" },
  { code: "+84",  name: "Vietnam",                iso: "VN" },
];

const DEFAULT_ISO = "IN"; // India default — primary market

/**
 * PhoneInput — accessible country-code dropdown + national-number field.
 *
 * Props:
 *   value        { code, number }   — controlled
 *   onChange     (next) => void
 *   required     boolean (mandates the national-number field; code always picked)
 *   testIdPrefix string  — used to build data-testid hooks
 *   label        string  — optional override label
 *
 * Stores ISO so the same code (e.g. +1 USA + Canada) stays unambiguous.
 */
export default function PhoneInput({ value, onChange, required = false, testIdPrefix = "phone", label }) {
  const current = value || { iso: DEFAULT_ISO, code: "+91", number: "" };
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = COUNTRY_CODES.find((c) => c.iso === current.iso) || COUNTRY_CODES.find((c) => c.iso === DEFAULT_ISO);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRY_CODES;
    return COUNTRY_CODES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.includes(q) ||
        c.iso.toLowerCase().includes(q),
    );
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const pick = (c) => {
    onChange?.({ iso: c.iso, code: c.code, number: current.number || "" });
    setOpen(false);
    setQuery("");
  };

  const onNumber = (e) => {
    onChange?.({ iso: current.iso, code: current.code, number: e.target.value.replace(/[^\d\s\-()]/g, "") });
  };

  return (
    <div className="relative" ref={ref} data-testid={`${testIdPrefix}-wrapper`}>
      {label && (
        <label className="label-mono">
          {label} {required && <span className="text-[var(--gold)]">*</span>}
        </label>
      )}
      <div className="flex gap-0 items-stretch">
        {/* Country code button */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 border-b border-[var(--border-strong)] bg-transparent pl-1 pr-2 py-3 text-[15px] text-[var(--navy)] hover:text-[var(--gold)] font-mono"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={`${testIdPrefix}-code-button`}
        >
          <span className="font-mono text-[13px] tracking-wider text-[var(--slate-light)]">{selected.iso}</span>
          <span className="font-mono text-[14px] font-semibold">{selected.code}</span>
          <ChevronDown size={13} className={"transition-transform " + (open ? "rotate-180" : "")} />
        </button>
        {/* National number */}
        <input
          type="tel"
          inputMode="numeric"
          required={required}
          value={current.number}
          onChange={onNumber}
          placeholder="0 0000 00000"
          className="input-editorial flex-1"
          data-testid={`${testIdPrefix}-number`}
        />
      </div>

      {open && (
        <div className="absolute z-30 mt-1 left-0 w-[320px] max-w-[90vw] bg-white border border-[var(--border-strong)] shadow-xl" data-testid={`${testIdPrefix}-dropdown`}>
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[var(--border)] bg-[var(--bg-2)]">
            <Search size={13} className="text-[var(--slate-light)]" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code…"
              className="flex-1 outline-none bg-transparent text-sm text-[var(--navy)] placeholder:text-[var(--slate-light)]"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <ul role="listbox" className="max-h-[260px] overflow-y-auto">
            {filtered.map((c) => (
              <li key={`${c.iso}-${c.code}`}>
                <button
                  type="button"
                  onClick={() => pick(c)}
                  className={"w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-[var(--navy)] hover:text-white " + (c.iso === current.iso ? "bg-[var(--bg-2)] text-[var(--navy)]" : "text-[var(--navy)]")}
                  data-testid={`${testIdPrefix}-option-${c.iso}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-[var(--slate-light)] w-7">{c.iso}</span>
                    <span>{c.name}</span>
                  </span>
                  <span className="font-mono text-[13px] text-[var(--gold)]">{c.code}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-center text-[12px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)]">
                No match
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Helper — joins a PhoneInput value into a single E.164-ish string for backend submit.
 */
export function formatPhone(p) {
  if (!p) return "";
  const num = (p.number || "").replace(/[^\d]/g, "");
  if (!num) return "";
  return `${p.code} ${num}`;
}
